import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LunchMenuitem } from "@mjolnerbacken/shared/types/lunch-menu-item";
import {
  getWeekOfDate,
  getWeeksOfYear,
} from "@mjolnerbacken/shared/utils/week";
import { ButtonGroup } from "primereact/buttongroup";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { H1 } from "../components/h1";
import { Layout } from "../components/layout";
import { PrimeButton } from "../components/prime/button";
import { useSchoolLunch } from "../hooks/school-lunch";
import { useLocale } from "../stores/locale";

export function SchoolLunch() {
  const t = useLocale((state) => state.locale);

  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();

  // If saturday or sunday, set to next week
  if (now.getDay() === 6 || now.getDay() === 0) {
    now.setDate(now.getDate() + 2);
  }

  const currentWeek = getWeekOfDate(now).toString();
  const currentYear = now.getFullYear().toString();

  const week = searchParams.get("week") ?? currentWeek.toString();
  const year = searchParams.get("year") ?? currentYear.toString();
  const school = searchParams.get("school") ?? "sundbyberg";

  const { data: schoolLunches } = useSchoolLunch({
    year: Number(year),
    week: Number(week),
    city: "sundbyberg",
  });

  useEffect(() => {
    if (
      !searchParams.get("week") ||
      !searchParams.get("year") ||
      !searchParams.get("school")
    ) {
      setSearchParams({ week, year, school });
    }
  }, [week, year, school, setSearchParams, searchParams]);

  return (
    <Layout>
      <div className="w-full mx-auto p-4">
        <div className="flex justify-center mb-4">
          <ButtonGroup>
            <PrimeButton
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
              route={
                Number(week) === 1
                  ? `/school-lunch?year=${Number(year) - 1}&week=${getWeeksOfYear(Number(year) - 1)}&school=${school}`
                  : `/school-lunch?year=${year}&week=${Number(week) - 1}&school=${school}`
              }
              tooltip={t.schoolLunch.previousWeek}
              tooltipOptions={{ position: "top" }}
            />
            <PrimeButton
              label={t.schoolLunch.weekNumber.replace("%WEEK%", week)}
              route={`/school-lunch?year=${currentYear}&week=${currentWeek}&school=${school}`}
              tooltip={
                year !== currentYear || week !== currentWeek
                  ? t.schoolLunch.now.replace("%WEEK%", currentWeek)
                  : undefined
              }
              tooltipOptions={{ position: "top" }}
            />
            <PrimeButton
              icon={<FontAwesomeIcon icon={faArrowRight} />}
              route={
                Number(week) === getWeeksOfYear(Number(year))
                  ? `/school-lunch?year=${Number(year) + 1}&week=1&school=${school}`
                  : `/school-lunch?year=${year}&week=${Number(week) + 1}&school=${school}`
              }
              tooltip={t.schoolLunch.nextWeek}
              tooltipOptions={{ position: "top" }}
            />
          </ButtonGroup>
        </div>
        <div className="mx-auto lg:w-2/3 w-full flex flex-col justify-center gap-2">
          {schoolLunches && schoolLunches.length > 0 ? (
            schoolLunches.map((lunch) => (
              <SchoolLunchItem key={lunch.date.toISOString()} lunch={lunch} />
            ))
          ) : (
            <div className="text-center text-gray-500">
              <em>{t.schoolLunch.noLunch}</em>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function SchoolLunchItem({ lunch }: { lunch: LunchMenuitem }) {
  const t = useLocale((state) => state.locale);

  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const day = days[lunch.date.getDay()];
  if (!day) {
    return null;
  }

  return (
    <div className="flex border border-gray-200 bg-gray-50 shadow-sm">
      <div className="bg-white w-1/5 flex flex-col items-center justify-center">
        <span>
          {lunch.date.getDate()}/{lunch.date.getMonth() + 1}
        </span>
        <span>{t.weekdays[day]}</span>
      </div>
      <div className="w-4/5 flex flex-col gap-1 p-2 bg-amber-100">
        <ul className="ml-6 list-[square] m-0 p-0 list-outside">
          {lunch.labels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
